import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Copy,
  FileSpreadsheet,
  FileText,
  Pencil,
  Plus,
  Printer,
  Search,
  Trash2,
  X,
  CreditCard,
  SearchX,
  Sparkles,
  ArrowLeft,
  Calendar,
  ArrowDownLeft,
  Wallet,
  Tag,
  User,
  IndianRupee,
  Hash,
  Save,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  ModalOverlay,
  ModalShell,
  ModalHero,
  ModalHeroGlow,
  ModalHeroRow,
  ModalHeroText,
  ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  FieldGrid,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldSelect,
  InlineSelect,
  ModalActions,
  SecondaryButton,
  PrimaryButton,
  type MenuCoords,
} from "./quotationMasterListShared";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { SubmitButton, FormLabelIcon, FormGradientDefs } from "../../molecules/Dashboard/Member/memberFormStyles";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { useAppSelector } from "../../../Redux/Hooks";
import type { RootState } from "../../../Redux/Store";
import { usePermissions } from "../../../hooks/usePermissions";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "./ListReportPrintTemplate";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import {
  getOrderTransactionsAPI,
  createOrderTransactionAPI,
  updateOrderTransactionAPI,
  deleteOrderTransactionAPI,
} from "../../../Redux/ApiCalls/Dashboard/OrderAPI";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  RemainingCell,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

type TransactionType = "Income" | "Expense";

type TransactionRecord = {
  id: string;
  transactionDate: string;
  type: TransactionType;
  account: string;
  category: string;
  orderNo: string;
  orderId?: number | null;
  consignee: string;
  creditAmount: string;
  debitAmount: string;
  reference: string;
  createdAt: string;
};

const TransactionPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const TransactionPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const TransactionTableScroll = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

type ModalProps = {
  record?: TransactionRecord | null;
  orders: any[];
  onSave: (data: any) => void;
  onClose: () => void;
};

const Req = styled.span`color: #e44862;`;

const TransactionModal: React.FC<ModalProps> = ({ record, orders, onSave, onClose }) => {
  const [form, setForm] = useState({
    transactionDate: record?.transactionDate ?? new Date().toISOString().split("T")[0],
    type: (record?.type ?? "Income") as TransactionType,
    account: record?.account ?? "Cash",
    category: record?.category ?? "Order Payment",
    orderNo: record?.orderNo && record.orderNo !== "N/A" ? record.orderNo : "",
    consignee: record?.consignee ?? "",
    creditAmount: record?.creditAmount && record.creditAmount !== "-" ? record.creditAmount : "",
    debitAmount: record?.debitAmount && record.debitAmount !== "-" ? record.debitAmount : "",
    reference: record?.reference ?? "",
  });

  const KNOWN_CATEGORIES = ["Order Payment", "Advance Payment", "Expense", "Refund"];
  const isOtherCategory = record?.category && !KNOWN_CATEGORIES.includes(record.category);
  const [otherCategory, setOtherCategory] = useState(isOtherCategory ? (record?.category ?? "") : "");
  const [categoryMode, setCategoryMode] = useState<string>(
    isOtherCategory ? "Other" : (record?.category ?? "Order Payment")
  );

  const set = (field: string, val: string) => {
    let sanitizedVal = val;
    if (field === "creditAmount" || field === "debitAmount") {
      if (val !== "") {
        const parsed = parseFloat(val);
        sanitizedVal = isNaN(parsed) ? "" : String(Math.max(0, parsed));
      }
    }
    setForm((p) => {
      const updated = { ...p, [field]: sanitizedVal };
      if (field === "orderNo" && val !== "") {
        const match = orders.find(o => o.order_no === val);
        if (match) {
          updated.consignee = match.customer_name;
        }
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!form.transactionDate) { showErrorToast("Transaction date is required"); return; }
    if (!form.consignee.trim()) { showErrorToast("Consignee is required"); return; }
    if (categoryMode === "Other" && !otherCategory.trim()) { showErrorToast("Please enter a custom category"); return; }

    let orderId = null;
    if (form.orderNo.trim()) {
      const match = orders.find(o => o.order_no.toLowerCase().trim() === form.orderNo.toLowerCase().trim());
      if (match) {
        orderId = match.id;
      } else {
        showErrorToast(`Order number ${form.orderNo} not found`);
        return;
      }
    }

    const effectiveCategory = categoryMode === "Other" ? otherCategory.trim() : categoryMode;

    const payload: any = {
      transaction_date: form.transactionDate,
      type: form.type,
      account: form.account,
      category: effectiveCategory,
      consignee: form.consignee,
      reference: form.reference,
      order: orderId,
    };

    if (form.type === "Income") {
      payload.credit_amount = form.creditAmount ? Number(form.creditAmount) : 0;
      payload.debit_amount = null;
    } else {
      payload.debit_amount = form.debitAmount ? Number(form.debitAmount) : 0;
      payload.credit_amount = null;
    }

    onSave(payload);
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <ModalHeroGlow />
            <ModalHeroRow>
              <ModalHeroText>
                <ModalHeroBadge><CreditCard size={11} /> Payment record</ModalHeroBadge>
                <ModalTitle>{record ? "Edit Transaction" : "Add New Transaction"}</ModalTitle>
                <ModalSubtitle>
                  {record ? "Update payment details for this order transaction." : "Record an incoming or outgoing order payment."}
                </ModalSubtitle>
              </ModalHeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </ModalHeroRow>
          </ModalHero>

          <ModalBody>
            <FormCard>
              <FieldGrid>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={Calendar} />
                    Transaction Date <Req>*</Req>
                  </FieldLabel>
                  <BrandDatePicker
                    value={form.transactionDate}
                    onChange={(v) => set("transactionDate", v)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={ArrowDownLeft} />
                    Type <Req>*</Req>
                  </FieldLabel>
                  <FieldSelect value={form.type} onChange={(e) => set("type", e.target.value)}>
                    <option value="Income">Income</option>
                    <option value="Expense">Expense</option>
                  </FieldSelect>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={Wallet} />
                    Account <Req>*</Req>
                  </FieldLabel>
                  <FieldSelect value={form.account} onChange={(e) => set("account", e.target.value)}>
                    <option value="Cash">Cash</option>
                    <option value="Google Pay">Google Pay</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                  </FieldSelect>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={Tag} />
                    Category
                  </FieldLabel>
                  <FieldSelect
                    value={categoryMode}
                    onChange={(e) => {
                      setCategoryMode(e.target.value);
                      if (e.target.value !== "Other") {
                        setOtherCategory("");
                        set("category", e.target.value);
                      }
                    }}
                  >
                    <option value="Order Payment">Order Payment</option>
                    <option value="Advance Payment">Advance Payment</option>
                    <option value="Expense">Expense</option>
                    <option value="Refund">Refund</option>
                    <option value="Other">Other</option>
                  </FieldSelect>
                  {categoryMode === "Other" && (
                    <FieldInput
                      placeholder="Enter custom category"
                      value={otherCategory}
                      onChange={(e) => setOtherCategory(e.target.value)}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={FileText} />
                    Order No
                  </FieldLabel>
                  <FieldSelect value={form.orderNo} onChange={(e) => set("orderNo", e.target.value)}>
                    <option value="">-- Select Order --</option>
                    {orders.map((o: any) => (
                      <option key={o.id} value={o.order_no}>
                        {o.order_no} - {o.customer_name}
                      </option>
                    ))}
                  </FieldSelect>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>
                    <FormLabelIcon icon={User} />
                    Consignee <Req>*</Req>
                  </FieldLabel>
                  <FieldInput placeholder="Customer / party name" value={form.consignee} onChange={(e) => set("consignee", e.target.value)} />
                </FieldGroup>
                {form.type === "Income" ? (
                  <FieldGroup>
                    <FieldLabel>
                      <FormLabelIcon icon={IndianRupee} />
                      Credit Amount (₹)
                    </FieldLabel>
                    <FieldInput type="number" min={0} placeholder="0.00" value={form.creditAmount} onChange={(e) => set("creditAmount", e.target.value)} />
                  </FieldGroup>
                ) : (
                  <FieldGroup>
                    <FieldLabel>
                      <FormLabelIcon icon={IndianRupee} />
                      Debit Amount (₹)
                    </FieldLabel>
                    <FieldInput type="number" min={0} placeholder="0.00" value={form.debitAmount} onChange={(e) => set("debitAmount", e.target.value)} />
                  </FieldGroup>
                )}
                <FieldGroup style={{ gridColumn: "1 / -1" }}>
                  <FieldLabel>
                    <FormLabelIcon icon={Hash} />
                    Reference
                  </FieldLabel>
                  <FieldInput placeholder="Optional reference note" value={form.reference} onChange={(e) => set("reference", e.target.value)} />
                </FieldGroup>
              </FieldGrid>
            </FormCard>
          </ModalBody>

          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton type="button" onClick={handleSubmit}>
              <Save size={15} />
              {record ? "Update Transaction" : "Save Transaction"}
            </PrimaryButton>
          </ModalActions>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────

const OrderTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectedBranch } = useAppSelector((state: RootState) => state.auth);
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canCreate = hasAnyPermission(["orders.update", "payments_management.create", "payments_management.update"]);
  const canUpdate = hasAnyPermission(["orders.update", "payments_management.update"]);
  const canDelete = hasAnyPermission(["orders.delete", "payments_management.delete"]);
  const hasAnyRowAction = canUpdate || canDelete;
  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [dbOrders, setDbOrders] = useState<any[]>([]);
  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<TransactionRecord | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordersReady, setOrdersReady] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Load orders to resolve FK mapping
  const loadOrders = async () => {
    try {
      let allOrders: any[] = [];
      let page = 1;
      let hasMore = true;
      const branchQuery = selectedBranch?.id ? `&branch_id=${selectedBranch.id}` : '';
      while (hasMore) {
        const res = await apiCall({ method: "GET", url: `/order/orders/?page=${page}&page_size=100${branchQuery}` });
        const list = res?.results || (Array.isArray(res) ? res : []);
        allOrders = [...allOrders, ...list];
        if (res?.next && list.length === 100) {
          page += 1;
        } else {
          hasMore = false;
        }
      }
      setDbOrders(allOrders);
    } catch { }
    finally {
      setOrdersReady(true);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await getOrderTransactionsAPI({
        page: currentPage,
        page_size: entries,
        search: searchText,
        branch_id: selectedBranch?.id,
      });
      const results = res?.results || res || [];
      setTotalCount(res?.count || (res?.results ? res.results.length : res?.length) || 0);
      const normalized = results.map((t: any) => {
        let orderNoStr = "N/A";
        if (t.order_no) {
          orderNoStr = t.order_no;
        } else if (t.order) {
          const match = dbOrders.find((o: any) => o.id === t.order);
          if (match) orderNoStr = match.order_no;
        }
        return {
          id: String(t.id),
          transactionDate: t.transaction_date,
          type: t.type,
          account: t.account,
          category: t.category,
          orderNo: orderNoStr,
          orderId: t.order,
          consignee: t.consignee,
          creditAmount: t.credit_amount ? String(t.credit_amount) : "-",
          debitAmount: t.debit_amount ? String(t.debit_amount) : "-",
          reference: t.reference || "",
          createdAt: new Date(t.created_at || Date.now()).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
        };
      });
      setTransactions(normalized);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to load transactions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initData = async () => {
      await loadOrders();
    };
    initData();
  }, [selectedBranch?.id]);

  useEffect(() => {
    if (ordersReady) {
      fetchTransactions();
    }
  }, [ordersReady, currentPage, entries, searchText, selectedBranch?.id]);

  const handleSave = async (payload: any) => {
    try {
      if (editRecord) {
        await updateOrderTransactionAPI(editRecord.id, payload);
        showSuccessToast("Transaction updated successfully");
      } else {
        await createOrderTransactionAPI(payload);
        showSuccessToast("Transaction added successfully");
      }
      fetchTransactions();
      setModalOpen(false);
      setEditRecord(null);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save transaction"));
    }
  };

  const handleEdit = (record: TransactionRecord) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null) return;
    try {
      await deleteOrderTransactionAPI(deleteConfirmId);
      showSuccessToast("Transaction deleted");
      fetchTransactions();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete transaction"));
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    copyDataToClipboard(
      transactions,
      ["Date", "Type", "Account", "Order No", "Consignee", "Credit", "Created At"],
      ["transactionDate", "type", "account", "orderNo", "consignee", "creditAmount", "createdAt"],
      "Transactions copied to clipboard"
    );
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToCSV(
      transactions,
      ["Date", "Type", "Account", "Category", "Order No", "Consignee", "Credit", "Debit", "Reference", "Created At"],
      ["transactionDate", "type", "account", "category", "orderNo", "consignee", "creditAmount", "debitAmount", "reference", "createdAt"],
      "transactions_export.csv",
      "CSV exported"
    );
  };

  const handleExportExcel = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToExcel(
      transactions,
      ["Date", "Type", "Account", "Category", "Order No", "Consignee", "Credit", "Debit", "Reference", "Created At"],
      ["transactionDate", "type", "account", "category", "orderNo", "consignee", "creditAmount", "debitAmount", "reference", "createdAt"],
      "transactions_export.xls",
      "Excel exported"
    );
  };

  const parseAmount = (value: string) => {
    if (!value || value === "-") return 0;
    return Number(String(value).replace(/[₹,\s]/g, "")) || 0;
  };

  const transactionReportRows = useMemo(
    () =>
      transactions.map((t) => {
        const creditRaw = parseAmount(t.creditAmount);
        const debitRaw = parseAmount(t.debitAmount);
        return {
          transactionDate: t.transactionDate || "—",
          type: t.type,
          type_key: t.type,
          account: t.account || "—",
          category: t.category || "—",
          orderNo: t.orderNo && t.orderNo !== "N/A" ? t.orderNo : "—",
          consignee: t.consignee || "—",
          creditAmount: creditRaw > 0 ? `₹${formatINR(creditRaw)}` : "—",
          debitAmount: debitRaw > 0 ? `₹${formatINR(debitRaw)}` : "—",
          credit_raw: creditRaw,
          debit_raw: debitRaw,
          reference: t.reference || "—",
        };
      }),
    [transactions]
  );

  const transactionReportSummary = useMemo(() => {
    const totalCredit = transactionReportRows.reduce((sum, row) => sum + row.credit_raw, 0);
    const totalDebit = transactionReportRows.reduce((sum, row) => sum + row.debit_raw, 0);
    return [
      { label: "Total Transactions", value: String(transactionReportRows.length) },
      { label: "Total Credit", value: `₹${formatINR(totalCredit)}` },
      { label: "Total Debit", value: `₹${formatINR(totalDebit)}`, highlight: true },
    ];
  }, [transactionReportRows]);

  const getReportTypeStyle = (type: string) =>
    type === "Income"
      ? { bg: "rgba(49,166,106,0.15)", color: "#2d8a55" }
      : { bg: "rgba(228,72,98,0.14)", color: "#c93a55" };

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "transactions_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visible = transactions;
  const totalPages = Math.ceil(totalCount / entries) || 1;
  const hasActiveSearch = !!searchText;

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[id];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 120;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  const activeMenuRecord = useMemo(
    () => visible.find((t) => t.id === activeMenuId) ?? null,
    [visible, activeMenuId]
  );

  return (
    <TransactionPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip type="button" onClick={() => navigate("/dashboard/orders/all")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge><CreditCard size={11} /> Order payments</HeroBadge>
              <PageTitle className="linear-text">Order Payment Transactions</PageTitle>
              <PageSubtitle>Track all incoming and outgoing order payments.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          {canCreate && (
            <SubmitButton
              type="button"
              $ready
              onClick={() => { setEditRecord(null); setModalOpen(true); }}
              style={{ minWidth: 200, height: 46 }}
            >
              <Plus size={16} /> Add New Transaction
            </SubmitButton>
          )}
        </HeroContent>
      </Hero>

      <TransactionPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                placeholder="Search transactions"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <InlineSelect value={entries} onChange={(e) => { setEntries(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </InlineSelect>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>

        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Transaction records</ListTitle>
          {!loading && (
            <CountBadge>
              {totalCount} record{totalCount !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : visible.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveSearch ? <SearchX size={36} strokeWidth={1.5} /> : <CreditCard size={36} strokeWidth={1.5} />}
            title={hasActiveSearch ? "No transactions found" : "No transactions yet"}
            description={
              hasActiveSearch
                ? "Nothing matched your search. Try a different order number, consignee, or reference."
                : "Record your first payment to start tracking incoming and outgoing order transactions."
            }
            actions={[
              ...(hasActiveSearch
                ? [{
                    label: "Clear search",
                    onClick: () => { setSearchVal(""); setSearchText(""); setCurrentPage(1); },
                    variant: "secondary" as const,
                  }]
                : []),
              ...(canCreate
                ? [{
                    label: hasActiveSearch ? "Add New Transaction" : "Add Your First Transaction",
                    onClick: () => { setEditRecord(null); setModalOpen(true); },
                    icon: <Plus size={15} />,
                  }]
                : []),
            ]}
            hint={!hasActiveSearch ? {
              icon: <Sparkles size={13} />,
              text: "Track income, expenses, advances & refunds",
            } : undefined}
          />
        ) : (
          <TransactionTableScroll ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={1300}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}><span>ID</span></th>
                  <th><span>Transaction Date</span></th>
                  <th><span>Type</span></th>
                  <th><span>Account</span></th>
                  <th><span>Category</span></th>
                  <th><span>Order No</span></th>
                  <th><span>Consignee</span></th>
                  <th><span>Credit Amount</span></th>
                  <th><span>Debit Amount</span></th>
                  <th><span>Reference</span></th>
                  <th><span>Created At</span></th>
                  {hasAnyRowAction && <th style={{ width: 100, textAlign: "right" }}><span>Action</span></th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((t: TransactionRecord, idx: number) => (
                  <tr key={t.id}>
                    <td style={{ color: "rgba(23,23,23,0.42)", fontWeight: 600 }}>
                      {(currentPage - 1) * entries + idx + 1}
                    </td>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{t.transactionDate}</td>
                    <td><TypeBadge $type={t.type}>{t.type}</TypeBadge></td>
                    <td style={{ whiteSpace: "nowrap" }}>{t.account}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{t.category}</td>
                    <td><OrderNo>{t.orderNo}</OrderNo></td>
                    <td style={{ whiteSpace: "nowrap" }}>{t.consignee}</td>
                    <td>
                      {t.creditAmount !== "-" && t.creditAmount
                        ? <AmountCell>₹{t.creditAmount}</AmountCell>
                        : <MutedDash>-</MutedDash>}
                    </td>
                    <td>
                      {t.debitAmount !== "-" && t.debitAmount
                        ? <RemainingCell>₹{t.debitAmount}</RemainingCell>
                        : <MutedDash>-</MutedDash>}
                    </td>
                    <td style={{ color: "rgba(23,23,23,0.55)" }}>{t.reference || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "rgba(23,23,23,0.6)" }}>{t.createdAt}</td>
                    {hasAnyRowAction && (
                      <td style={{ textAlign: "right" }}>
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[t.id] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === t.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMenu(t.id);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              </PaymentsTable>
            </TableWrap>
          </TransactionTableScroll>
        )}

        {!loading && visible.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            Showing {totalCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalCount)} of {totalCount} entries
          </FooterText>
          <Pagination>
            <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</PageButton>
            {Array.from({ length: totalPages }).map((_, i) => (
              <PageButton key={i} $active={currentPage === i + 1} onClick={() => setCurrentPage(i + 1)}>{i + 1}</PageButton>
            ))}
            <PageButton disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</PageButton>
          </Pagination>
        </FooterRow>
      </TransactionPanel>

      {activeMenuRecord && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {canUpdate && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    handleEdit(activeMenuRecord);
                  }}
                >
                  <ItemIconContainer>
                    <Pencil size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Edit</ItemLabel>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    setDeleteConfirmId(activeMenuRecord.id);
                  }}
                >
                  <ItemIconContainer>
                    <Trash2 size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Delete</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        )}

      {/* Add / Edit Modal */}
      {modalOpen && (
        <TransactionModal
          record={editRecord}
          orders={dbOrders}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditRecord(null); }}
        />
      )}
      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this transaction?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
        />
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Order Payment Transactions"
        subtitle="Income and expense transactions overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "transactionDate", label: "Date", width: "11%" },
          {
            key: "type",
            label: "Type",
            align: "center",
            width: "10%",
            render: (value, row) => {
              const style = getReportTypeStyle(String(row.type_key || ""));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
          { key: "account", label: "Account", width: "12%" },
          { key: "category", label: "Category", width: "12%" },
          { key: "orderNo", label: "Order No", width: "11%" },
          { key: "consignee", label: "Consignee", width: "14%" },
          {
            key: "creditAmount",
            label: "Credit",
            align: "right",
            width: "11%",
            render: (value) => (
              <CellBold style={{ color: String(value) !== "—" ? "#2d8a55" : undefined }}>
                {String(value)}
              </CellBold>
            ),
          },
          {
            key: "debitAmount",
            label: "Debit",
            align: "right",
            width: "11%",
            render: (value) => (
              <CellBold style={{ color: String(value) !== "—" ? "#c93a55" : undefined }}>
                {String(value)}
              </CellBold>
            ),
          },
          { key: "reference", label: "Reference", width: "12%" },
        ]}
        rows={transactionReportRows}
        summaryItems={transactionReportSummary}
      />
    </TransactionPage>
  );
};

export default OrderTransactionsPage;

// ─── Styled Components ─────────────────────────────────────────────────────

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 0 22px;
  flex-shrink: 0;
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => p.$type === "Income" ? "rgba(49,166,106,0.12)" : "rgba(228,72,98,0.12)"};
  color:      ${(p) => p.$type === "Income" ? "#31a66a" : "#e44862"};
`;

const OrderNo = styled.span`font-weight: 700; white-space: nowrap;`;
const MutedDash = styled.span`color: rgba(23,23,23,0.35); font-weight: 500;`;